import { formatMinorUnits } from '@seek/currency';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Stack,
  Heading,
  TooltipRenderer,
  IconInfo,
  Button,
  Badge,
  List,
  IconTick,
  Text,
} from 'braid-design-system';
import { useLocation, useNavigate } from 'react-router';

import { useAppContext } from 'src/App/context/AppContext';
import { useTracking } from 'src/App/context/TrackingProvider';

import {
  type AdBudgetSelectionPageBaseEvent,
  createBuyNowPressedEvent,
} from '../../eventTracking/createEvent';
import type { BudgetOption } from '../../mapBudgetOption';

import translations from './.vocab';

interface BudgetPanelProps {
  budgetOption: BudgetOption;
  trackingBaseEvent: AdBudgetSelectionPageBaseEvent;
  mostNumberOfDiscounts: number;
  productSetId: string;
  showBuyNowButton: boolean;
}

export const BudgetPanel = ({
  budgetOption,
  trackingBaseEvent,
  mostNumberOfDiscounts,
  productSetId,
  showBuyNowButton,
}: BudgetPanelProps) => {
  const { t } = useTranslations(translations);
  const { trackLink } = useTracking();

  const { language } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const numberOfDiscounts = budgetOption.discounts?.length ?? 0;
  const discountPadding = Array.from(
    { length: mostNumberOfDiscounts - numberOfDiscounts },
    (_, index) => (
      <Box
        key={index}
        marginTop={{ desktop: 'xsmall', tablet: 'none' }}
        marginBottom={{ desktop: 'xsmall', tablet: 'none' }}
      />
    ),
  );

  const budgetDisplayPrice =
    budgetOption.tax?.value &&
    budgetOption.tax.displayRecommendation === 'inclusive'
      ? budgetOption.budgetValue + budgetOption.tax.value
      : budgetOption.budgetValue;

  const isCompactPrice = budgetDisplayPrice.toString().length >= 9;

  let taxDescription = '';

  if (budgetOption.tax?.displayRecommendation === 'inclusive') {
    taxDescription = `${t('Incl.')} ${budgetOption.tax?.code}`;
  }

  if (budgetOption.tax?.displayRecommendation === 'exclusive') {
    taxDescription = `+${budgetOption.tax?.code}`;
  }

  return (
    <Box
      boxShadow="borderNeutralLight"
      borderRadius="xlarge"
      paddingX="gutter"
      paddingY="xlarge"
      background="surface"
      maxWidth="content"
    >
      <Stack space="medium">
        <Stack space="small">
          <Heading align="left" level="3">
            {t(budgetOption.adPostingFrequency)}
          </Heading>
          <Box>
            <Text tone="secondary" maxLines={2}>
              {t('Post {min}-{max} job ads over...', {
                minimum: budgetOption.adRange.minimum.toString(),
                maximum: budgetOption.adRange.maximum.toString(),
                duration: budgetOption.duration.value.toString(),
              })}{' '}
              <TooltipRenderer
                id="adRangeInfo"
                placement="bottom"
                tooltip={<Text>{t('The exact number of ads you...')}</Text>}
              >
                {({ triggerProps }) => (
                  <Box aria-label="Help" {...triggerProps} display="inline">
                    <IconInfo />
                  </Box>
                )}
              </TooltipRenderer>
            </Text>
          </Box>
        </Stack>

        <Stack space="xxsmall">
          {budgetOption.discounts?.map((discount) => {
            if (discount.type === 'FixedAmount') {
              return (
                <Badge tone="promote" key="FixedDiscount">
                  {t('{amount} off {adType} features', {
                    amount: formatMinorUnits(discount.value, {
                      currency: budgetOption.currency,
                      locale: language,
                      dropFraction: true,
                    }),
                    adType: discount.adType,
                  })}
                </Badge>
              );
            }
            if (discount.type === 'Percentage') {
              return (
                <Badge tone="promote" key="PercentageDiscount">
                  {t('{amount} off {adType} ads', {
                    amount: `${discount.value}%`,
                    adType: discount.adType,
                  })}
                </Badge>
              );
            }
          })}
          {discountPadding}
        </Stack>

        <Stack space="small">
          <Heading level={isCompactPrice ? '2' : '1'} align="left">
            {formatMinorUnits(budgetDisplayPrice, {
              currency: budgetOption.currency,
              locale: language,
              dropFraction: true,
            })}
          </Heading>
          <Text size="small" tone="secondary" align="left">
            {taxDescription}
          </Text>
        </Stack>
        {showBuyNowButton && (
          <Button
            tone="formAccent"
            variant="ghost"
            onClick={() => {
              trackLink({
                eventName: 'seek_contract_buy_now_pressed',
                data: {
                  ...trackingBaseEvent,
                  ...createBuyNowPressedEvent(budgetOption),
                },
              });

              const searchParams = new URLSearchParams(location?.search);
              if (!searchParams.has('productId')) {
                searchParams.append('productId', budgetOption.budgetId);
              }

              navigate(
                {
                  pathname: 'checkout',
                  search: searchParams.toString(),
                },
                {
                  state: {
                    pathname: 'products/ad-budgets/checkout',
                    productId: budgetOption.budgetId,
                    productSetId,
                  },
                },
              );
            }}
          >
            {t('Buy Now')}
          </Button>
        )}
        <List type="icon" space="gutter" icon={<IconTick tone="formAccent" />}>
          {budgetOption.budgetType !== 'PremiumBudget' && (
            <Text tone="neutral" size="standard">
              {Array.isArray(budgetOption.purchasableProducts) &&
                budgetOption.purchasableProducts.length > 1 &&
                t('Post {productListExceptLast} and {lastProduct} ads', {
                  productListExceptLast: budgetOption.purchasableProducts
                    .slice(0, -1)
                    .join(', '),
                  lastProduct: budgetOption.purchasableProducts.slice(-1)[0],
                })}
              {Array.isArray(budgetOption.purchasableProducts) &&
                budgetOption.purchasableProducts.length === 1 &&
                t('Post {product} ads', {
                  product: budgetOption.purchasableProducts[0],
                })}
              {budgetOption.purchasableProducts === 'Any' &&
                t('Post any ad type')}
            </Text>
          )}

          {budgetOption.budgetType === 'PremiumBudget' ? (
            <>
              <Text tone="neutral" size="standard">
                {t('use this budget exclusively for premium ads')}
              </Text>
              <Text tone="neutral" size="standard">
                {t('Other ad types will be charged separately')}
              </Text>
              <Text tone="neutral" size="standard">
                {t('discount applies for {duration} or until...', {
                  duration: budgetOption.duration.value.toString(),
                  durationUnit: t(budgetOption.duration.unit),
                })}
              </Text>
            </>
          ) : (
            <Text tone="neutral" size="standard">
              {t('Discount applies for {duration}...', {
                duration: budgetOption.duration.value,
                durationUnit: t(budgetOption.duration.unit),
              })}
            </Text>
          )}
        </List>
      </Stack>
    </Box>
  );
};
